/**
 * A/B Test Statistical Engine
 *
 * Comprehensive statistical utilities for A/B test analysis including:
 * - Two-proportion Z-test for significance
 * - Confidence interval calculation
 * - Sample size estimation (MDE-based)
 * - Test duration estimation
 * - Health scoring and readiness assessment
 */

import { cumulativeStdNormalProbability } from './stats'
import { normalCDFInverse } from './abTestCalculations'

// ============================================================================
// Core Types
// ============================================================================

export type TestType = 'engagement' | 'conversion' | 'revenue' | 'funnel'

/**
 * Below this many visitors a day a test is flagged as low traffic: the
 * planner and the results page both say so, and the running verdict asks the
 * user to keep the test running instead of calling it.
 */
export const LOW_TRAFFIC_DAILY_VISITORS = 100

export function isLowDailyTraffic(dailyVisitors: number): boolean {
  return dailyVisitors < LOW_TRAFFIC_DAILY_VISITORS
}

/**
 * What one visitor contributes to the goal. `proportion`: converted or not (form
 * submit, destination page). `count`: any number of events (the click goal counts
 * every click, so 0.94 clicks per visitor is a rate, not a share of visitors).
 */
export type GoalMetric = 'proportion' | 'count'

export interface MetricOptions {
  metric?: GoalMetric
  /**
   * Count metrics only: variance over mean of the event count (1 = Poisson).
   * Clicks cluster on a few visitors, so the real variance is higher than
   * Poisson; see estimateCountDispersion.
   */
  dispersion?: number
}

/** Goals whose number is an event count per visitor, not a conversion share. */
export function isCountGoal(goal: string | null | undefined): boolean {
  return goal === 'click_through' || goal === 'click_through_rate' || goal === 'Click Through Rate'
}

/** Below this many degrees of freedom the dispersion estimate is noise; use Poisson. */
const MIN_DISPERSION_DF = 4
const MAX_DISPERSION = 25

/**
 * Quasi-Poisson dispersion from daily buckets: Pearson chi-square over degrees of
 * freedom, per group (variant) against that group's own rate. Each day sums many
 * visitors, so when a few visitors click many times the day totals swing more
 * than Poisson allows and this ratio rises above 1. Day-of-week swings also push
 * it up, which only makes the test more careful. Never below 1.
 */
export function estimateCountDispersion(
  groups: Array<Array<{ events: number; exposure: number }>>
): number {
  let chiSquare = 0
  let df = 0
  for (const buckets of groups) {
    const used = buckets.filter((b) => b.exposure > 0)
    const events = used.reduce((sum, b) => sum + b.events, 0)
    const exposure = used.reduce((sum, b) => sum + b.exposure, 0)
    if (used.length < 2 || events <= 0 || exposure <= 0) continue
    const rate = events / exposure
    for (const b of used) {
      const expected = b.exposure * rate
      chiSquare += (b.events - expected) ** 2 / expected
    }
    df += used.length - 1
  }
  if (df < MIN_DISPERSION_DF) return 1
  return Math.min(MAX_DISPERSION, Math.max(1, chiSquare / df))
}

export interface TestTypeConfig {
  label: string
  description: string
  significance: number
  minDuration: number
  maxDuration: number
  mdeRange: [number, number]
  sampleSizeRange: [number, number]
  icon: string
  tips: string[]
}

export const TEST_TYPE_CONFIGS: Record<TestType, TestTypeConfig> = {
  engagement: {
    label: 'Simple Engagement Test',
    description: 'Clicks, scrolls, time on page',
    significance: 90,
    minDuration: 3,
    maxDuration: 30,
    mdeRange: [5, 10],
    sampleSizeRange: [1000, 2000],
    icon: 'engagement',
    tips: [
      'Engagement tests converge quickly with moderate traffic',
      'Focus on metrics directly impacted by the change',
      'Consider testing during consistent traffic periods',
    ],
  },
  conversion: {
    label: 'Conversion Test',
    description: 'Form submissions, purchases',
    significance: 95,
    minDuration: 7,
    maxDuration: 30,
    mdeRange: [10, 20],
    sampleSizeRange: [5000, 10000],
    icon: 'conversion',
    tips: [
      'Conversion tests require more traffic for reliable results',
      'Avoid calling tests early even if trends look promising',
      'Run for at least one full business cycle (7 days)',
    ],
  },
  revenue: {
    label: 'Revenue Optimization',
    description: 'AOV, LTV impact',
    significance: 97,
    minDuration: 14,
    maxDuration: 30,
    mdeRange: [20, 30],
    sampleSizeRange: [10000, 50000],
    icon: 'revenue',
    tips: [
      'Revenue tests need long durations to account for purchase cycles',
      'Consider seasonality when choosing test periods',
      'Segment results by customer type for deeper insights',
    ],
  },
  funnel: {
    label: 'Multi-Step Funnel',
    description: 'Multi-step funnel optimization',
    significance: 95,
    minDuration: 14,
    maxDuration: 30,
    mdeRange: [15, 25],
    sampleSizeRange: [8000, 20000],
    icon: 'funnel',
    tips: [
      'Measure each funnel step independently',
      'Account for drop-off compounding across steps',
      'Test one step at a time for clearer attribution',
    ],
  },
}

// ============================================================================
// Two-Proportion Z-Test
// ============================================================================

export interface ZTestResult {
  zScore: number
  pValue: number
  significancePercent: number
  isSignificant: boolean
  confidenceLevel: number
}

/**
 * Perform a two-proportion Z-test comparing control vs. variant conversion rates.
 * Returns z-score, p-value, and significance as a percentage.
 */
export function twoProportionZTest(
  controlConversions: number,
  controlSamples: number,
  variantConversions: number,
  variantSamples: number,
  threshold: number = 95,
  options: MetricOptions = {}
): ZTestResult {
  if (controlSamples === 0 || variantSamples === 0) {
    return { zScore: 0, pValue: 1, significancePercent: 0, isSignificant: false, confidenceLevel: 0 }
  }

  const p1 = controlConversions / controlSamples
  const p2 = variantConversions / variantSamples
  const pPooled = (controlConversions + variantConversions) / (controlSamples + variantSamples)

  let se: number
  if (options.metric === 'count') {
    // Two-sample rate test: under "no difference" both arms share the pooled rate,
    // variance λ·φ per visitor. Binomial p(1−p) would shrink to near 0 at 0.94
    // clicks per visitor and call noise significant.
    const dispersion = Math.max(1, options.dispersion ?? 1)
    se = Math.sqrt(dispersion * pPooled * (1 / controlSamples + 1 / variantSamples))
  } else if (p1 > 1 || p2 > 1) {
    // Rates above 1 cannot be shares; Poisson SE avoids NaN from p*(1−p) < 0.
    // Callers that know the goal is a count should pass metric: 'count'.
    se = Math.sqrt(p1 / controlSamples + p2 / variantSamples)
  } else {
    se = Math.sqrt(pPooled * (1 - pPooled) * (1 / controlSamples + 1 / variantSamples))
  }
  if (se === 0 || isNaN(se)) {
    return { zScore: 0, pValue: 1, significancePercent: 0, isSignificant: false, confidenceLevel: 0 }
  }

  const zScore = (p2 - p1) / se
  const absZ = Math.abs(zScore)

  // Two-tailed p-value
  const pValue = 2 * (1 - cumulativeStdNormalProbability(absZ))
  const significancePercent = Math.min((1 - pValue) * 100, 99.99)
  const isSignificant = significancePercent >= threshold

  return {
    zScore,
    pValue,
    significancePercent,
    isSignificant,
    confidenceLevel: significancePercent,
  }
}

// ============================================================================
// Confidence Interval
// ============================================================================

export interface ConfidenceIntervalResult {
  controlRate: number
  variantRate: number
  controlCI: { lower: number; upper: number }
  variantCI: { lower: number; upper: number }
  liftEstimate: number
  liftCI: { lower: number; upper: number }
}

/**
 * Calculate confidence intervals for both variants and the lift.
 */
export function calculateConfidenceIntervals(
  controlConversions: number,
  controlSamples: number,
  variantConversions: number,
  variantSamples: number,
  confidenceLevel: number = 0.95,
  options: MetricOptions = {}
): ConfidenceIntervalResult {
  const zAlpha = normalCDFInverse(1 - (1 - confidenceLevel) / 2)

  const p1 = controlSamples > 0 ? controlConversions / controlSamples : 0
  const p2 = variantSamples > 0 ? variantConversions / variantSamples : 0

  // Count metrics (and any rate above 1) use the rate SE sqrt(φ·λ/n) instead of
  // sqrt(p*(1−p)/n), which is too narrow near 1 and NaN above it.
  const isRateMetric = options.metric === 'count' || p1 > 1 || p2 > 1
  const dispersion = options.metric === 'count' ? Math.max(1, options.dispersion ?? 1) : 1
  const se1 = controlSamples > 0
    ? Math.sqrt(isRateMetric ? (dispersion * p1) / controlSamples : (p1 * (1 - p1)) / controlSamples)
    : 0
  const se2 = variantSamples > 0
    ? Math.sqrt(isRateMetric ? (dispersion * p2) / variantSamples : (p2 * (1 - p2)) / variantSamples)
    : 0

  const controlCI = {
    lower: Math.max(0, p1 - zAlpha * se1),
    upper: isRateMetric ? p1 + zAlpha * se1 : Math.min(1, p1 + zAlpha * se1),
  }

  const variantCI = {
    lower: Math.max(0, p2 - zAlpha * se2),
    upper: isRateMetric ? p2 + zAlpha * se2 : Math.min(1, p2 + zAlpha * se2),
  }

  // Lift (relative improvement)
  const liftEstimate = p1 > 0 ? (p2 - p1) / p1 : 0
  const seLift = p1 > 0
    ? Math.sqrt((se2 * se2) / (p1 * p1) + (p2 * p2 * se1 * se1) / (p1 * p1 * p1 * p1))
    : 0
  const liftCI = {
    lower: liftEstimate - zAlpha * seLift,
    upper: liftEstimate + zAlpha * seLift,
  }

  return {
    controlRate: p1,
    variantRate: p2,
    controlCI,
    variantCI,
    liftEstimate,
    liftCI,
  }
}

// ============================================================================
// Sample Size Estimation
// ============================================================================

interface SampleSizeEstimate {
  perVariant: number
  total: number
  estimatedDays: number | null
}

/**
 * Estimate required sample size per variant, given baseline rate and MDE.
 */
export function estimateSampleSize(
  baselineRate: number,
  mdePercent: number,
  significance: number = 95,
  power: number = 80,
  options: MetricOptions = {}
): SampleSizeEstimate {
  const alpha = 1 - significance / 100
  const beta = 1 - power / 100

  const zAlpha = normalCDFInverse(1 - alpha / 2)
  const zBeta = normalCDFInverse(1 - beta)

  if (options.metric === 'count') {
    // Rate comparison: n = φ·(zα + zβ)²·(λ1 + λ2) / (λ2 − λ1)² per variant.
    // No clamp: 0.94 clicks per visitor + 20% is 1.13, not 0.99.
    const l1 = baselineRate
    const l2 = baselineRate * (1 + mdePercent / 100)
    const dispersion = Math.max(1, options.dispersion ?? 1)
    if (l1 <= 0 || l2 === l1) return { perVariant: Infinity, total: Infinity, estimatedDays: null }
    const perVariant = Math.ceil((dispersion * (zAlpha + zBeta) ** 2 * (l1 + l2)) / (l2 - l1) ** 2)
    return { perVariant, total: perVariant * 2, estimatedDays: null }
  }

  // Clamp to [0,1] for binomial variance. Count goals pass metric: 'count' above.
  const p1 = Math.min(baselineRate, 0.99)
  const p2 = Math.min(baselineRate * (1 + mdePercent / 100), 0.99)

  const numerator = Math.pow(
    zAlpha * Math.sqrt(2 * p1 * (1 - p1)) + zBeta * Math.sqrt(p1 * (1 - p1) + p2 * (1 - p2)),
    2
  )
  const denominator = Math.pow(p2 - p1, 2)

  if (denominator === 0) {
    return { perVariant: Infinity, total: Infinity, estimatedDays: null }
  }

  const perVariant = Math.ceil(numerator / denominator)
  return { perVariant, total: perVariant * 2, estimatedDays: null }
}

// ============================================================================
// Test Duration Estimation
// ============================================================================

export interface DurationEstimate {
  days: number
  requiredSamplePerVariant: number
  totalRequired: number
  isLowTraffic: boolean
  isVeryLong: boolean
  estimatedMonths: number
}

/**
 * Estimate test duration based on traffic volume and required sample size.
 */
export function estimateTestDuration(
  dailyVisitors: number,
  baselineRate: number,
  mdePercent: number,
  significance: number = 95,
  numVariants: number = 2,
  options: MetricOptions = {}
): DurationEstimate {
  const { perVariant } = estimateSampleSize(baselineRate, mdePercent, significance, 80, options)

  const totalRequired = perVariant * numVariants
  const visitorsPerVariant = dailyVisitors / numVariants
  const days = visitorsPerVariant > 0 ? Math.ceil(perVariant / visitorsPerVariant) : Infinity

  return {
    days: isFinite(days) ? days : 9999,
    requiredSamplePerVariant: perVariant,
    totalRequired,
    isLowTraffic: isLowDailyTraffic(dailyVisitors),
    isVeryLong: days > 90,
    estimatedMonths: isFinite(days) ? Math.round((days / 30) * 10) / 10 : 99,
  }
}

// ============================================================================
// Test Health Assessment
// ============================================================================

export type HealthStatus = 'healthy' | 'warning' | 'critical' | 'not_started'
export type ReadinessState = 'keep_running' | 'ready_to_call' | 'inconclusive' | 'not_started'

export interface TestHealthMetrics {
  currentSamples: number
  requiredSamples: number
  sampleProgress: number
  daysRunning: number
  recommendedMinDays: number
  durationProgress: number
  significanceLevel: number
  significanceColor: 'green' | 'yellow' | 'red'
  healthStatus: HealthStatus
  readiness: ReadinessState
  readinessLabel: string
  readinessDescription: string
  warnings: string[]
  zTestResult: ZTestResult
  confidenceIntervals: ConfidenceIntervalResult | null
}

/**
 * Compute a holistic health assessment of an active A/B test.
 */
export function assessTestHealth(params: {
  controlConversions: number
  controlSamples: number
  variantConversions: number
  variantSamples: number
  daysRunning: number
  dailyVisitors: number
  baselineRate: number
  mdePercent: number
  targetSignificance: number
  testType: TestType
  metric?: GoalMetric
  dispersion?: number
}): TestHealthMetrics {
  const {
    controlConversions,
    controlSamples,
    variantConversions,
    variantSamples,
    daysRunning,
    dailyVisitors,
    baselineRate,
    mdePercent,
    targetSignificance,
    testType,
  } = params
  const metricOptions: MetricOptions = { metric: params.metric, dispersion: params.dispersion }

  const config = TEST_TYPE_CONFIGS[testType]
  const currentSamples = controlSamples + variantSamples
  const { perVariant: requiredPerVariant } = estimateSampleSize(baselineRate, mdePercent, targetSignificance, 80, metricOptions)
  const requiredSamples = requiredPerVariant * 2
  const sampleProgress = requiredSamples > 0 ? Math.min((currentSamples / requiredSamples) * 100, 100) : 0

  const recommendedMinDays = config.minDuration
  const durationProgress = recommendedMinDays > 0 ? Math.min((daysRunning / recommendedMinDays) * 100, 100) : 0

  const zTestResult = twoProportionZTest(
    controlConversions, controlSamples,
    variantConversions, variantSamples,
    targetSignificance,
    metricOptions
  )

  const significanceLevel = zTestResult.significancePercent
  const significanceColor: 'green' | 'yellow' | 'red' =
    significanceLevel >= 95 ? 'green' : significanceLevel >= 90 ? 'yellow' : 'red'

  // Confidence intervals
  let confidenceIntervals: ConfidenceIntervalResult | null = null
  if (currentSamples > 0) {
    confidenceIntervals = calculateConfidenceIntervals(
      controlConversions, controlSamples,
      variantConversions, variantSamples,
      targetSignificance / 100,
      metricOptions
    )
  }

  // Warnings
  const warnings: string[] = []
  if (isLowDailyTraffic(dailyVisitors)) {
    warnings.push(`Low traffic detected (~${dailyVisitors} visitors/day) - this test may take longer than expected`)
  }
  const duration = estimateTestDuration(dailyVisitors, baselineRate, mdePercent, targetSignificance, 2, metricOptions)
  if (duration.isVeryLong) {
    warnings.push(`Estimated time to significance: ${duration.estimatedMonths} months`)
  }
  if (daysRunning > 7 && sampleProgress < 25) {
    warnings.push(`Test running for ${daysRunning} days but only ${Math.round(sampleProgress)}% to required sample size`)
  }
  if (daysRunning > config.maxDuration && significanceLevel < targetSignificance) {
    warnings.push(`Test exceeded recommended max duration of ${config.maxDuration} days without reaching significance`)
  }

  // Health status
  let healthStatus: HealthStatus = 'healthy'
  if (currentSamples === 0) {
    healthStatus = 'not_started'
  } else if (warnings.length >= 2 || (daysRunning > config.maxDuration * 2)) {
    healthStatus = 'critical'
  } else if (warnings.length >= 1) {
    healthStatus = 'warning'
  }

  // Readiness assessment
  let readiness: ReadinessState = 'keep_running'
  let readinessLabel = 'Keep Running'
  let readinessDescription = 'Continue collecting data to reach statistical significance.'

  if (currentSamples === 0) {
    readiness = 'not_started'
    readinessLabel = 'Awaiting Data'
    readinessDescription = 'No traffic has been recorded yet.'
  } else if (significanceLevel >= targetSignificance && sampleProgress >= 80 && daysRunning >= recommendedMinDays) {
    readiness = 'ready_to_call'
    readinessLabel = 'Ready to Call'
    readinessDescription = `This test has reached ${significanceLevel.toFixed(1)}% significance with sufficient data.`
  } else if (sampleProgress < 30 && daysRunning > config.maxDuration) {
    readiness = 'inconclusive'
    readinessLabel = 'Inconclusive'
    readinessDescription = 'Sample size is too small to draw reliable conclusions.'
  }

  return {
    currentSamples,
    requiredSamples,
    sampleProgress,
    daysRunning,
    recommendedMinDays,
    durationProgress,
    significanceLevel,
    significanceColor,
    healthStatus,
    readiness,
    readinessLabel,
    readinessDescription,
    warnings,
    zTestResult,
    confidenceIntervals,
  }
}

// ============================================================================
// Post-Test Result Interpretation
// ============================================================================

export type ResultState =
  | 'significant_winner'
  | 'trending_positive'
  | 'no_difference'
  | 'inconclusive'

export interface TestResultInterpretation {
  state: ResultState
  icon: string
  headline: string
  description: string
  recommendation: string
  explanations: string[]
}

/**
 * Interpret test results and produce human-readable guidance.
 */
export function interpretTestResults(params: {
  significanceLevel: number
  targetSignificance: number
  sampleProgress: number
  liftEstimate: number
  daysRunning: number
  minDuration: number
}): TestResultInterpretation {
  const { significanceLevel, targetSignificance, sampleProgress, liftEstimate, daysRunning, minDuration } = params

  const explanations = [
    `Statistical significance measures how confident we are that the observed difference is real and not due to chance. At ${targetSignificance}% significance, there's only a ${(100 - targetSignificance).toFixed(0)}% chance the result is a false positive.`,
    `We need ${targetSignificance}% confidence before making decisions because calling a test too early increases the risk of implementing changes that don't actually improve performance (Type I error).`,
    'False positives are common when tests are called prematurely. The more times you check results, the higher the chance of seeing a "winner" that isn\'t real.',
  ]

  if (significanceLevel >= targetSignificance && sampleProgress >= 80) {
    return {
      state: 'significant_winner',
      icon: '✅',
      headline: 'Statistically significant - safe to implement winner',
      description: `The test has reached ${significanceLevel.toFixed(1)}% confidence with a ${(liftEstimate * 100).toFixed(1)}% lift. This result is statistically reliable.`,
      recommendation: 'You can confidently implement the winning variant. The observed improvement is unlikely to be due to chance.',
      explanations,
    }
  }

  if (significanceLevel >= targetSignificance * 0.9 && liftEstimate > 0) {
    return {
      state: 'trending_positive',
      icon: '⚠️',
      headline: 'Trending positive but not yet significant - recommend running longer',
      description: `Current significance is ${significanceLevel.toFixed(1)}%, approaching the ${targetSignificance}% threshold. The variant shows a ${(liftEstimate * 100).toFixed(1)}% improvement trend.`,
      recommendation: daysRunning < minDuration
        ? `Run for at least ${minDuration - daysRunning} more days to reach the minimum duration.`
        : 'Continue running to collect more data. The trend is promising but needs more evidence.',
      explanations,
    }
  }

  if (sampleProgress < 50) {
    return {
      state: 'inconclusive',
      icon: '📊',
      headline: 'Inconclusive - sample size too small',
      description: `Only ${Math.round(sampleProgress)}% of the required sample size has been collected. Results are not yet reliable.`,
      recommendation: 'Keep the test running. More data is needed before any conclusions can be drawn.',
      explanations,
    }
  }

  return {
    state: 'no_difference',
    icon: '❌',
    headline: 'No significant difference detected',
    description: `After collecting ${Math.round(sampleProgress)}% of the target sample, no statistically significant difference has been detected between the variants.`,
    recommendation: 'Consider testing more dramatic changes, targeting higher-traffic pages, or combining with qualitative research (heatmaps, user feedback).',
    explanations,
  }
}

// ============================================================================
// Insufficient Traffic Detection
// ============================================================================

export interface TrafficWarning {
  severity: 'low' | 'moderate' | 'critical'
  estimatedDuration: DurationEstimate
  message: string
  recommendations: string[]
}

/**
 * Detect whether traffic is sufficient for a meaningful test.
 */
export function detectInsufficientTraffic(
  dailyVisitors: number,
  baselineRate: number,
  mdePercent: number,
  significance: number = 95,
  options: MetricOptions = {}
): TrafficWarning | null {
  const duration = estimateTestDuration(dailyVisitors, baselineRate, mdePercent, significance, 2, options)
  const weeklyVisitors = dailyVisitors * 7

  const recommendations = [
    'Consider testing higher-traffic pages first',
    'Test larger changes that produce bigger effects',
    'Combine this with qualitative research (heatmaps, user feedback)',
    'Run test anyway to gather directional insights',
  ]

  if (isLowDailyTraffic(dailyVisitors) || duration.days > 180) {
    return {
      severity: 'critical',
      estimatedDuration: duration,
      message: `Based on your current traffic (~${weeklyVisitors.toLocaleString()} visitors/week) and target improvement (${mdePercent}%), this test will take approximately ${duration.estimatedMonths} months to reach statistical significance.`,
      recommendations,
    }
  }

  if (duration.days > 90) {
    return {
      severity: 'moderate',
      estimatedDuration: duration,
      message: `Based on your traffic (~${weeklyVisitors.toLocaleString()} visitors/week), this test may take ${duration.estimatedMonths} months. Consider increasing the MDE or testing higher-traffic pages.`,
      recommendations: recommendations.slice(0, 3),
    }
  }

  if (duration.days > 60) {
    return {
      severity: 'low',
      estimatedDuration: duration,
      message: `This test is estimated to take about ${Math.ceil(duration.days / 7)} weeks with your current traffic. This is feasible but longer than ideal.`,
      recommendations: recommendations.slice(0, 2),
    }
  }

  return null
}

// ============================================================================
// Sample Ratio Mismatch (SRM)
// ============================================================================

export type SampleRatioMismatch = {
  hasMismatch: boolean
  chiSquare: number
  pValue: number
  expectedShares: number[]
  observedShares: number[]
  message: string | null
}

/**
 * Chi-square goodness-of-fit check for sample ratio mismatch.
 * Flags when observed traffic shares diverge from the intended split
 * (p < 0.001 is the common industry threshold for SRM).
 */
export function detectSampleRatioMismatch(
  observedCounts: number[],
  expectedShares?: number[],
  alpha: number = 0.001
): SampleRatioMismatch {
  const n = observedCounts.length
  const total = observedCounts.reduce((a, b) => a + b, 0)
  const shares =
    expectedShares && expectedShares.length === n
      ? expectedShares
      : Array.from({ length: n }, () => 1 / n)

  if (total === 0 || n < 2) {
    return {
      hasMismatch: false,
      chiSquare: 0,
      pValue: 1,
      expectedShares: shares,
      observedShares: shares.map(() => 0),
      message: null,
    }
  }

  let chiSquare = 0
  for (let i = 0; i < n; i++) {
    const expected = shares[i] * total
    if (expected <= 0) continue
    const diff = observedCounts[i] - expected
    chiSquare += (diff * diff) / expected
  }

  // df = n - 1; approximate p-value via chi-square survival for df=1 (2 variants)
  // or a coarse table for higher df. Good enough for a guardrail flag.
  const df = n - 1
  let pValue: number
  if (df === 1) {
    // χ²(1) = Z² → p = 2*(1-Φ(√χ²))
    const z = Math.sqrt(Math.max(chiSquare, 0))
    pValue = 2 * (1 - cumulativeStdNormalProbability(z))
  } else {
    // Rough Wilson–Hilferty approximation
    const h = Math.pow(chiSquare / df, 1 / 3)
    const mu = 1 - 2 / (9 * df)
    const sigma = Math.sqrt(2 / (9 * df))
    const z = (h - mu) / sigma
    pValue = 1 - cumulativeStdNormalProbability(z)
  }

  const observedShares = observedCounts.map((c) => c / total)
  const hasMismatch = pValue < alpha

  return {
    hasMismatch,
    chiSquare,
    pValue,
    expectedShares: shares,
    observedShares,
    message: hasMismatch
      ? `Sample ratio mismatch detected (p=${pValue.toExponential(2)}). Traffic is not splitting as configured. Results may be unreliable until this is fixed.`
      : null,
  }
}
